/**
 * LA COURBE D'OBJECTIF DES ÉCONOMIES D'EAU.
 *
 * Posée par-dessus l'histogramme de projection entre deux tours, elle dit si
 * la salle est sur la bonne voie : au tour n, elle doit atteindre À TERME
 * (dernière colonne) `n × target_per_round` — 20 points par tour joué, donc
 * 60 en fin d'atelier à trois tours, 100 à cinq.
 *
 * LA FORME EST CELLE DE L'ANCIENNE PLATEFORME, reprise à l'identique pour
 * cinq tours et généralisée à N :
 *
 *   colonne k < N :   T × k × min(r, k) / N
 *   colonne k ≥ N :   T × min(r, N) / (1 + ε_k)
 *
 * où r est le nombre de tours validés et ε décroît de 0,2 à 0 sur les colonnes
 * restantes. L'ancien code écrivait cette queue avec des diviseurs 6, 5.5, 5.3,
 * 5.2, 5.1, 5 — soit 5 × (1 + ε) : ce sont les RATIOS qu'on garde, pas les
 * diviseurs, pour que la colonne N vaille toujours 83 % du plafond quel que
 * soit N. Étirée sur plus ou moins de colonnes, la courbe garde son allure.
 *
 * Tout est calculé ici, hors du rendu : le graphe pose la ligne, le badge dit
 * le verdict, et aucun des deux ne fait d'arithmétique.
 */

export const DEFAULT_TARGET_PER_ROUND = 20;

/** Écart à l'objectif en deçà duquel on est « dans l'objectif » : ±10 %. */
export const ON_TRACK_TOLERANCE = 0.1;

/* Les ratios de la queue, de la colonne N à la dernière. Ceux de l'ancienne
   plateforme, exactement : 100/6, 100/5.5, 100/5.3, 100/5.2, 100/5.1, 100/5. */
const TAIL_RATIOS = [0.2, 0.1, 0.06, 0.04, 0.02, 0];

/** Interpolation linéaire de `points` rééchantillonnés sur `count` valeurs. */
function resample(points, count) {
  if (count <= 1) return [points[points.length - 1]];
  const last = points.length - 1;
  return Array.from({ length: count }, (_, j) => {
    const t = (j * last) / (count - 1);
    const i = Math.min(Math.floor(t), last - 1);
    const frac = t - i;
    return points[i] + frac * (points[i + 1] - points[i]);
  });
}

/**
 * Le nombre de tours validés. `current_round` est posé par le serveur à
 * chaque page à décisions ; les parties commencées avant ce champ ont, au
 * mieux, un journal des mesures dont chaque entrée porte son tour.
 */
export function roundsPlayed(gameStatus) {
  const direct = Number(gameStatus?.current_round);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const fromLog = (gameStatus?.decision_log || [])
    .map((entry) => Number(entry?.round))
    .filter((round) => Number.isFinite(round) && round > 0);
  return fromLog.length ? Math.max(...fromLog) : 0;
}

/**
 * Les points visés par tour : le réglage de la page s'il y en a un, sinon
 * celui de l'atelier (recopié dans la partie à son lancement), sinon 20.
 * La courbe et le bilan de fin passent tous deux par ici — deux lectures
 * séparées finiraient par annoncer deux objectifs.
 */
export function targetPerRoundOf(gameStatus, pageSetting) {
  return (
    Number(pageSetting) ||
    Number(gameStatus?.target_per_round) ||
    DEFAULT_TARGET_PER_ROUND
  );
}

/**
 * La courbe elle-même : un entier par colonne. `null` si l'atelier ne déclare
 * pas son nombre de tours — sans N, la formule n'a pas de sens.
 */
export function objectiveCurve({ columns, rounds, roundsPlayed: played, targetPerRound }) {
  const n = Math.min(Number(rounds) || 0, Number(columns) || 0);
  if (n < 1) return null;
  const r = Math.max(0, Math.min(Number(played) || 0, n));
  const t = Number(targetPerRound) || DEFAULT_TARGET_PER_ROUND;
  const head = Array.from({ length: n - 1 }, (_, i) => {
    const k = i + 1;
    return (t * k * Math.min(r, k)) / n;
  });
  const plateau = t * r;
  const tail = resample(TAIL_RATIOS, columns - n + 1).map((ratio) => plateau / (1 + ratio));
  return [...head, ...tail].map((v) => Math.round(v));
}

/** « En avance », « dans l'objectif » ou « en retard », à ±10 % près. */
export function objectiveVerdict(value, target) {
  if (!Number.isFinite(value) || !Number.isFinite(target) || target <= 0) return null;
  const gap = value - target;
  if (Math.abs(gap) <= ON_TRACK_TOLERANCE * target) return 'on_track';
  return gap > 0 ? 'ahead' : 'behind';
}

export const VERDICT_LABELS = {
  ahead: 'En avance',
  on_track: "Dans l'objectif",
  behind: 'En retard',
};

/**
 * Tout ce que le graphe a besoin de savoir, en une passe : la courbe, la cible
 * à terme, la valeur obtenue et le verdict. `null` quand il n'y a rien à
 * tracer — atelier sans nombre de tours, ou aucun tour encore validé (une
 * ligne à zéro sur l'axe ne dit rien).
 */
export function waterObjective(gameStatus, { targetPerRound, scoreId = 'water_projection' } = {}) {
  const score = (gameStatus?.score_dic || {})[scoreId];
  const metadata = score?.metadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const series = Object.values(metadata).filter(Array.isArray);
  const columns = series.length ? series[0].length : 0;
  const played = roundsPlayed(gameStatus);
  if (!columns || played < 1) return null;

  const rounds = Number(gameStatus?.nb_rounds_cg) || 0;
  const perRound = targetPerRoundOf(gameStatus, targetPerRound);
  const curve = objectiveCurve({ columns, rounds, roundsPlayed: played, targetPerRound: perRound });
  if (!curve) return null;

  const target = curve[curve.length - 1];
  const value = Number.isFinite(Number(score.value))
    ? Number(score.value)
    : series.reduce((sum, values) => sum + (Number(values[values.length - 1]) || 0), 0);
  return {
    curve,
    target,
    value,
    verdict: objectiveVerdict(value, target),
    roundsPlayed: played,
    rounds: Math.min(rounds, columns),
    targetPerRound: perRound,
  };
}
